import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getCategoryBySlug, getCollectionsByCategory } from '@/lib/supabase';
import { Collection as CollectionType } from '@/lib/supabase';
import Navbar from '@/components/Navbar';
import { useToast } from '@/components/ui/use-toast';
import { Card } from '@/components/ui/card';
import { Heart } from 'lucide-react';

const CategoryPage: React.FC = () => {
  const { slug } = useParams();
  const { toast } = useToast();
  const [categoryTitle, setCategoryTitle] = useState('');
  const [collections, setCollections] = useState<CollectionType[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    if (slug) {
      loadCategoryData(slug);
    }
  }, [slug]);

  const loadCategoryData = async (slug: string) => {
    try {
      const category = await getCategoryBySlug(slug);
      if (!category) {
        toast({
          title: "Error",
          description: "Category not found",
          variant: "destructive"
        });
        return;
      }

      setCategoryTitle(category.title);
      const collections = await getCollectionsByCategory(category.id);
      setCollections(collections);
      setIsLoading(false);
    } catch (error) {
      console.error('Error loading category:', error);
      toast({
        title: "Error",
        description: "Failed to load category",
        variant: "destructive"
      });
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="container mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold mb-6">Category: {categoryTitle}</h1>

        {isLoading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="bg-card animate-pulse h-64 rounded-lg border border-border" />
            ))}
          </div>
        ) : collections.length === 0 ? (
          <p className="text-center text-muted-foreground">No collections found in this category.</p>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {collections.map(collection => (
              <Link 
                key={collection.id}
                to={`/collection/${collection.public_id}`}
                className="block group"
              >
                <Card className="overflow-hidden transition-all duration-200 hover:shadow-lg">
                  <div className="aspect-video relative">
                    <div className="grid grid-cols-2 gap-0.5 absolute inset-0">
                      {collection.videos?.slice(0, 4).map((video, idx) => (
                        <div key={idx} className="overflow-hidden">
                          <img
                            src={video.thumbnail}
                            alt={video.title}
                            className="w-full h-full object-cover"
                            loading="lazy"
                          />
                        </div>
                      ))}
                    </div>
                    <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <span className="text-white font-medium">View Collection</span>
                    </div>
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between">
                      <h3 className="font-medium truncate">{collection.name}</h3>
                      {collection.is_subscribed && (
                        <Heart size={16} className="text-accent fill-current" />
                      )}
                    </div>
                    {collection.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2 mt-1">
                        {collection.description}
                      </p>
                    )}
                    {collection.tags && collection.tags.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {collection.tags.map(tag => (
                          <span
                            key={tag}
                            className="text-xs bg-accent/10 text-accent-foreground px-2 py-0.5 rounded-full"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </Card>
              </Link>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default CategoryPage;